import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ErrorHandler } from '../Errors/ErrorHandler';
import { IdentificationStep } from './IdentificationStep';
import { OtpStep } from './OtpStep';
import { RecordView } from './RecordView';
import { RecordNotice } from './RecordNotice';
import { OtpResult, PageStatus, getResult, notices } from './constants';
import '../../styles/medicalRecord.css';

const Step = {
    Loading: 'loading',
    Identify: 'identify',
    Otp: 'otp',
    Record: 'record',
    Notice: 'notice'
}

// Outcomes that end the flow - the patient has to get a new link from the clinic.
const terminalNotice = {
    [OtpResult.Blocked]: notices.blocked,
    [OtpResult.LinkNotFound]: notices.deadLink,
    [OtpResult.LinkExpired]: notices.expired
}

const noticeByPageStatus = {
    [PageStatus.Expired]: notices.expired,
    [PageStatus.NotFound]: notices.deadLink
}

export const MedicalRecord = () => {
    const { id } = useParams();

    const [step, setStep] = useState(Step.Loading);
    const [status, setStatus] = useState(200);
    const [notice, setNotice] = useState(null);
    const [clinicLogoUrl, setClinicLogoUrl] = useState(null);
    const [record, setRecord] = useState(null);
    const [delivery, setDelivery] = useState(null);

    // Both the id number and the record token live in memory only: the token is a bearer
    // credential, and CAL4 needs the id number again on the verify call.
    const [govId, setGovId] = useState("");

    const showNotice = useCallback((value) => {
        setNotice(value);
        setStep(Step.Notice);
    }, []);

    const applyPageStatus = useCallback((pageStatus) => {
        const terminal = noticeByPageStatus[pageStatus];
        if (terminal) {
            showNotice(terminal);
            return;
        }

        // RequiresVerification - a live link, or a session that ran out.
        setStep(Step.Identify);
    }, [showNotice]);

    const loadLinkInfo = useCallback(async () => {
        try {
            const { data } = await axios.get(`/medicalrecord/info/${id}`);
            setClinicLogoUrl(data.clinicLogoUrl);
            applyPageStatus(data.status);
        } catch (error) {
            const response = error.response;
            if (response?.status === 404) {
                applyPageStatus(response.data?.status ?? PageStatus.NotFound);
                return;
            }

            setStatus(response?.status ?? 500);
        }
    }, [id, applyPageStatus]);

    useEffect(() => {
        id && loadLinkInfo();
    }, [id, loadLinkInfo]);

    const loadRecord = async (token) => {
        try {
            const { data } = await axios.get(`/medicalrecord/get/${id}`, {
                headers: { 'X-Record-Token': token }
            });

            if (data.status === PageStatus.Ready) {
                setClinicLogoUrl(data.clinicLogoUrl);
                setRecord(data);
                setStep(Step.Record);
                return;
            }

            applyPageStatus(data.status);
        } catch (error) {
            const response = error.response;
            if (response?.status === 401) {
                // Session ran out - identify again, this is not an error screen.
                setStep(Step.Identify);
                return;
            }

            if (response?.status === 404) {
                applyPageStatus(response.data?.status ?? PageStatus.NotFound);
                return;
            }

            setStatus(response?.status ?? 500);
        }
    }

    /// Runs one otp call and routes its result: terminal outcomes take over the screen,
    /// everything else goes back to the step so it can mark its own field.
    const runOtpCall = async (call) => {
        let data;
        let httpStatus;

        try {
            const response = await call();
            data = response.data;
            httpStatus = response.status;
        } catch (error) {
            data = error.response?.data;
            httpStatus = error.response?.status;
        }

        const result = getResult(data, httpStatus);
        const terminal = terminalNotice[result];
        if (terminal) {
            showNotice(terminal);

            return { ok: false, result, handled: true };
        }

        return { ok: result === OtpResult.Success, result, data };
    }

    const sendCode = async () => {
        const outcome = await runOtpCall(() => axios.post(`/medicalrecord/sendcode/${id}`, { govId }));
        if (outcome.ok) {
            setDelivery(outcome.data);
            outcome.data.clinicLogoUrl && setClinicLogoUrl(outcome.data.clinicLogoUrl);
            setStep(Step.Otp);
        }

        return outcome;
    }

    const verifyCode = async (code) => {
        const outcome = await runOtpCall(() => axios.post(`/medicalrecord/verifycode/${id}`, { govId, code }));
        if (outcome.ok) {
            await loadRecord(outcome.data.token);
        }

        return outcome;
    }

    if (status !== 200) {
        return <ErrorHandler status={status} />
    }

    switch (step) {
        case Step.Identify:
            return <IdentificationStep
                clinicLogoUrl={clinicLogoUrl}
                govId={govId}
                onGovIdChange={setGovId}
                onSubmit={sendCode} />
        case Step.Otp:
            return <OtpStep
                clinicLogoUrl={clinicLogoUrl}
                delivery={delivery}
                onSubmit={verifyCode}
                onResend={sendCode} />
        case Step.Record:
            return <RecordView record={record} clinicLogoUrl={clinicLogoUrl} />
        case Step.Notice:
            return <RecordNotice notice={notice} clinicLogoUrl={clinicLogoUrl} />
        case Step.Loading:
        default:
            return null;
    }
}
